import React from 'react'

import { pxToRem } from '@/shared/styles/themes'
import { styled } from '@mui/material'

import { TextField } from '../TextField/TextField'
import { TextFieldLabel } from '../TextField/TextFieldLabel'
import { AddressFieldMap } from './AddressFieldMap'
import { AddressFieldMapControl } from './AddressFieldMapControl'

export type MapFieldValue = {
  latitude?: number
  longitude?: number
  zoomLevel?: number
}

export type MapFieldProps = {
  label?: string
  value?: MapFieldValue
  defaultValue?: MapFieldValue
  disabled?: boolean
  required?: boolean
  error?: boolean
  helperText?: string
  onChange: (value: MapFieldValue) => void
}

export const MapField: React.FC<MapFieldProps> = ({
  label = 'Map Location',
  value,
  disabled,
  required,
  error,
  helperText,
  onChange,
}) => {
  const handleChange = (key: keyof MapFieldValue, newValue: number) => {
    onChange({ ...value, [key]: newValue })
  }

  const handleMapRightClick = (lat: number, lng: number, zoomLevel?: number) => {
    if (!disabled) {
      onChange({
        latitude: lat,
        longitude: lng,
        zoomLevel: zoomLevel,
      })
    }
  }

  return (
    <StyledContainer>
      <StyledCoordinatesRow>
        <TextField
          label="Latitude"
          type="number"
          disabled={disabled}
          required={required}
          value={value?.latitude || ''}
          onChange={(e) => handleChange('latitude', parseFloat(e.target.value))}
          error={error || (value?.latitude !== undefined && (value.latitude < -90 || value.latitude > 90))}
          helperText={
            error ? helperText :
              (value?.latitude !== undefined && (value.latitude < -90 || value.latitude > 90))
                ? 'Latitude must be between -90 and 90'
                : ''
          }
          inputProps={{ min: -90, max: 90, step: 'any' }}
          sx={{ flex: 6 }}
        />
        <TextField
          label="Longitude"
          type="number"
          disabled={disabled}
          required={required}
          value={value?.longitude || ''}
          onChange={(e) => handleChange('longitude', parseFloat(e.target.value))}
          error={error || (value?.longitude !== undefined && (value.longitude < -180 || value.longitude > 180))}
          helperText={
            error ? helperText :
              (value?.longitude !== undefined && (value.longitude < -180 || value.longitude > 180))
                ? 'Longitude must be between -180 and 180'
                : ''
          }
          inputProps={{ min: -180, max: 180, step: 'any' }}
          sx={{ flex: 6 }}
        />
      </StyledCoordinatesRow>

      <TextFieldLabel sx={{ marginTop: pxToRem(8) }}>
        {label} (right click on the map to set latitude and longitude, scroll to set zoom level)
      </TextFieldLabel>

      <AddressFieldMap>
        <AddressFieldMapControl
          latitude={value?.latitude}
          longitude={value?.longitude}
          zoomLevel={value?.zoomLevel}
          onMapRightClick={handleMapRightClick}
        />
      </AddressFieldMap>
    </StyledContainer>
  )
}

const StyledContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledCoordinatesRow = styled('div')`
  ${({ theme }) => theme.breakpoints.up('md')} {
    display: flex;
    flex-direction: row;
    gap: ${(props) => props.theme.typography.pxToRem(8)};
  }
`